/**
 * @file     leadFlowRoutes.ts
 * @purpose  Builds typed URL helpers for navigating into each lead flow entry point (buyer, seller, presale, find tenants) with optional query parameters. When featureFlags.dealFinderPersonaIntros is ON, the helpers point at the new /deal-finder/{persona} intro pages so internal CTAs land on the persona-scoped onramp; when OFF, the legacy form-host URL is preserved.
 */
import { routes } from './routes';
import { featureFlags } from '@/core/config/featureFlags';
import type { DealFinderIntent } from '@/core/types';

export type BuyerLeadJourney = Extract<
  DealFinderIntent,
  'buy-home' | 'move-to-vancouver' | 'investment-opportunities' | 'presale' | 'just-researching'
>;

const withQuery = (path: string, params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    }
  });

  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
};

const PERSONA_INTROS_ON = featureFlags.dealFinderPersonaIntros;

export const leadFlowRoutes = {
  /**
   * Universal entry. Always /deal-finder.
   * - flag OFF: variant-resolved buyer-leaning landing
   * - flag ON: trifork chooser (Buy / Sell / Find Tenants)
   */
  chooser: routes.dealFinder,

  /**
   * Buyer entry. Internal links labelled "Get started", "Personalize my
   * search", etc. should target this. The journey arg deep-links into the
   * buyer flow with a pre-set journey context.
   * - flag OFF: legacy form host with ?flow=buyer
   * - flag ON: buyer persona intro page (`/deal-finder/buyer`), which has its
   *   own "Personalize my search" CTA into the form. journey arg is forwarded
   *   so the eventual form mount picks up the right pre-fill.
   */
  buyer: (journey?: BuyerLeadJourney) => {
    if (PERSONA_INTROS_ON) {
      return withQuery('/deal-finder/buyer', { journey });
    }
    return withQuery(routes.dealFinder, {
      flow: 'buyer',
      journey,
    });
  },

  /**
   * Seller entry.
   * - flag OFF: legacy /home-value with ?flow=seller
   * - flag ON: seller persona intro (`/deal-finder/seller`)
   */
  seller: () => {
    if (PERSONA_INTROS_ON) {
      return '/deal-finder/seller';
    }
    return withQuery(routes.homeValue, { flow: 'seller' });
  },

  /**
   * Convenience for presale-flavored buyer entry.
   */
  presale: () => {
    if (PERSONA_INTROS_ON) {
      return withQuery('/deal-finder/buyer', { journey: 'presale' });
    }
    return withQuery(routes.dealFinder, {
      flow: 'buyer',
      journey: 'presale',
    });
  },

  /**
   * Landlord entry.
   * - flag OFF: legacy /find-tenants with ?flow=landlord
   * - flag ON: landlord persona intro (`/deal-finder/landlord`)
   */
  findTenants: () => {
    if (PERSONA_INTROS_ON) {
      return '/deal-finder/landlord';
    }
    return withQuery(routes.findTenants, { flow: 'landlord' });
  },
} as const;
